import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"

/**
 * Pulls readable messages out of an A2P endpoint error response:
 * {detail}, DRF field errors ({field: [msg]} or nested {section: {field: [msg]}}),
 * or Twilio evaluation results ({twilio_error: [...]}) handled by the caller.
 */
export function a2pErrorMessages(error) {
  const data = error?.response?.data
  if (!error?.response) {
    return ["We couldn't reach OneBase. Check your connection and try again."]
  }
  if (!data || typeof data !== "object") {
    return ["Something went wrong. Please try again or contact support."]
  }
  if (typeof data.detail === "string") {
    return [data.detail]
  }
  const messages = []
  function collect(value, label) {
    if (Array.isArray(value)) {
      value.forEach((item) =>
        typeof item === "string"
          ? messages.push(label ? `${label}: ${item}` : item)
          : collect(item, label)
      )
    } else if (value && typeof value === "object") {
      Object.entries(value).forEach(([key, inner]) =>
        collect(inner, FIELD_LABELS[key] || key.replaceAll("_", " "))
      )
    } else if (typeof value === "string") {
      messages.push(label ? `${label}: ${value}` : value)
    }
  }
  const { twilio_error: _ignored, ...rest } = data
  collect(rest)
  return messages.length
    ? messages
    : ["Something went wrong. Please try again or contact support."]
}

/** Field names in error responses -> labels used in our forms. */
export const FIELD_LABELS = {
  business_name: "Legal business name",
  business_registration_number: "EIN",
  business_registration_identifier: "Tax ID type",
  business_type: "Business type",
  business_industry: "Business industry",
  website_url: "Website",
  social_media_profile_urls: "Social media profile",
  mobile_phone_number: "Mobile phone number",
  phone_number: "Phone number",
  first_name: "First name",
  last_name: "Last name",
  email: "Email",
  brand_name: "Brand name",
  street: "Street",
  city: "City",
  region: "State",
  postal_code: "ZIP code",
  iso_country: "Country",
  business: "Business",
  sole_prop: "Details",
  address: "Address",
  representative: "Representative",
  non_field_errors: "",
}

export default function A2PFormErrors({ messages }) {
  if (!messages?.length) {
    return null
  }
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      {messages.map((message, i) => (
        <Typography
          key={i}
          component="li"
          variant="body1"
          color="error.main"
          sx={{ fontWeight: 600 }}
        >
          {message}
        </Typography>
      ))}
    </Box>
  )
}
